/**
 * TablesScreen — карта зала (§4.1). Статусы столов из живого POS: свободен /
 * занят / требует внимания (висящий заказ). Сгруппировано по залам. Обновление
 * поллингом (событий канала для столов пока нет).
 */
import { useCallback, useEffect, useState } from "react";
import type { TableStatus } from "@/types/contract";
import { api, UnauthorizedError } from "@/api/client";
import { ScreenHeader } from "@/components/ScreenHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { AlertIcon } from "@/components/ui/icons";

interface Props {
  restaurantId: string;
}

type Phase =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

const POLL_MS = 20_000;

const STATUS_META: Record<
  TableStatus["status"],
  { label: string; cls: string }
> = {
  free: { label: "Свободен", cls: "border-border bg-card text-muted" },
  occupied: { label: "Занят", cls: "border-ink/20 bg-ink/5 text-ink" },
  attention: {
    label: "Внимание",
    cls: "border-danger/40 bg-danger/10 text-danger",
  },
};

function fmtOpen(min: number | null): string | null {
  if (min == null) return null;
  if (min < 60) return `${min} мин`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h} ч` : `${Math.floor(h / 24)} дн`;
}

export function TablesScreen({ restaurantId }: Props) {
  const [tables, setTables] = useState<TableStatus[]>([]);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setPhase({ kind: "loading" });
      try {
        const list = await api.getTables({ restaurant_id: restaurantId });
        setTables(list);
        setPhase({ kind: "ready" });
      } catch (e) {
        const message =
          e instanceof UnauthorizedError
            ? "Сессия истекла — войдите в Orees Cloud снова."
            : e instanceof Error
              ? e.message
              : "Неизвестная ошибка";
        setPhase({ kind: "error", message });
      }
    },
    [restaurantId],
  );

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const byDept = groupByDept(tables);
  const busy = tables.filter((t) => t.status !== "free").length;

  return (
    <>
      <ScreenHeader
        eyebrow="Главный экран"
        title="Карта зала"
        subtitle={
          phase.kind === "ready"
            ? `${tables.length} столов · занято ${busy}`
            : "Статусы столов из POS"
        }
      />

      {phase.kind === "error" && (
        <Card className="flex flex-col items-center gap-3 py-10 text-center">
          <AlertIcon size={26} className="text-danger" />
          <h3 className="text-base font-semibold text-ink">
            Не удалось загрузить карту зала
          </h3>
          <p className="max-w-sm text-sm text-muted">{phase.message}</p>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Повторить
          </Button>
        </Card>
      )}

      {phase.kind === "ready" && tables.length === 0 && (
        <Card className="py-10 text-center text-sm text-muted">
          Столы не заданы в POS.
        </Card>
      )}

      {phase.kind !== "error" &&
        byDept.map(([dept, list]) => (
          <section key={dept} className="mb-6">
            <h2 className="mb-2 text-sm font-semibold text-muted">{dept}</h2>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
              {list.map((t) => {
                const meta = STATUS_META[t.status];
                const open = fmtOpen(t.opened_min);
                return (
                  <div
                    key={`${dept}-${t.code}`}
                    className={`flex flex-col items-center justify-center gap-0.5 rounded-control border p-3 text-center ${meta.cls}`}
                    title={meta.label}
                  >
                    <span className="tabular text-lg font-semibold">
                      {t.code}
                    </span>
                    <span className="text-[11px] leading-tight">
                      {meta.label}
                    </span>
                    {open && (
                      <span className="text-[10px] opacity-70">{open}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
    </>
  );
}

function groupByDept(tables: TableStatus[]): [string, TableStatus[]][] {
  const map = new Map<string, TableStatus[]>();
  for (const t of tables) {
    const d = t.department ?? "Без зала";
    (map.get(d) ?? map.set(d, []).get(d)!).push(t);
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], "ru"));
}
